<section class="bg-star-gray-600 pt-[40px] pb-[60px] xl:pt-[64px] xl:pb-[100px]">
  <div class="container">
    <div class="flex flex-col gap-[40px] lg:flex-row lg:items-center lg:gap-[60px]">
      <div class="flex max-w-[720px] flex-col">
        <h1 class="text-[29px] leading-[1.25] text-white md:text-[40px] xl:text-[44px]">
          AML API for Crypto Wallet Screening and Monitoring
        </h1>
        <p class="mt-[24px] text-[16px] leading-[1.8] text-[#C0C0C0] md:text-[18px]">
          One integration instead of four provider contracts. The AMLOfficer AML API brings wallet
          screening and transaction monitoring — backed by four AML data providers, including BitOK,
          Global Ledger and Beosin — into the systems your team already runs.
        </p>
        <div class="mt-[32px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
          <a
            [href]="appLinks.createAccountHref()"
            target="_blank"
            class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
          >
            <span class="text-[16px] tracking-[0.3px]">Register Business Account</span>
            <mat-icon
              svgIcon="second-arrow-right"
              class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
            ></mat-icon>
          </a>
          <span class="text-[14px] leading-[1.6] text-white/50">
            API access is issued with a business account.
          </span>
        </div>
      </div>
      <div class="flex justify-center lg:max-w-[392px]">
        <picture>
          <source srcset="./images/aml-api-desktop.png" media="(min-width: 992px)" />
          <source srcset="./images/aml-api-mobile.png" media="(max-width: 991px)" />
          <img
            src="./images/aml-api-desktop.png"
            alt=""
            class="w-full max-w-[392px] rounded-[10px] object-cover object-center"
          />
        </picture>
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="max-w-[820px] text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      One integration instead of four
    </h2>
    <p class="mt-[16px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
      Working with AML data directly means a separate contract, a separate key and a separate
      response format for every provider — and a decision about what to do when two of them
      disagree. That work does not make your product better; it just has to be done before your
      product can screen anything.
    </p>
    <p class="mt-[16px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
      AMLOfficer aggregates four AML data providers behind one account, so your side of the
      integration is a single API. Providers draw on different datasets, and an address one of them
      calls clean is regularly flagged by another — with aggregation, that disagreement reaches your
      risk logic instead of being decided by whichever vendor you happened to sign with.
    </p>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      Two ways to work with the API
    </h2>
    <div class="mt-[24px] grid gap-[20px] md:grid-cols-2">
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Fast API</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Access reports from your dashboard and collaborate with your team instantly. The lighter
          path: your people work in the AMLOfficer interface, and the API keeps the reports flowing
          where they are needed.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Pro API</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Integrate AML data directly into your systems in real time — for full control and advanced
          use cases, where screening has to happen inside your own product rather than beside it.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container flex flex-col gap-[40px] lg:flex-row lg:gap-[60px]">
    <div class="flex-1">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        What the integration is used for
      </h2>
      <ul class="mt-[16px] flex flex-col gap-[14px]">
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Screening an address at the point where your product decides — an onboarding step, a
            deposit screen, a payout queue.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Putting wallets under continuous monitoring from your own system instead of adding them
            by hand.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Pulling AML reports into your back office, so a compliance record lives next to the
            transaction it belongs to.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Keeping people in the loop: the same account still gives your team the dashboard, role
            management and Telegram alerts.
          </span>
        </li>
      </ul>
    </div>
    <div class="flex-1">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        How the integration starts
      </h2>
      <ol class="mt-[16px] flex flex-col gap-[16px]">
        <li class="flex items-start gap-[16px]">
          <span
            class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
            >1</span
          >
          <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
            Open a business account — the API belongs to the account, so registration comes first.
          </p>
        </li>
        <li class="flex items-start gap-[16px]">
          <span
            class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
            >2</span
          >
          <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
            Choose the mode that fits the job: reports through the Fast API, or AML data in your own
            systems through the Pro API.
          </p>
        </li>
        <li class="flex items-start gap-[16px]">
          <span
            class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
            >3</span
          >
          <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
            Get your credentials and technical documentation with the account, and connect them in
            your environment. Setup on our side is designed as a one-day step.
          </p>
        </li>
        <li class="flex items-start gap-[16px]">
          <span
            class="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#25282D] text-[16px] font-bold text-white"
            >4</span
          >
          <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
            Go live with support: AMLOfficer provides personalised compliance support, so your
            developers are not reading a specification alone.
          </p>
        </li>
      </ol>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      Who integrates AML screening
    </h2>
    <div class="mt-[24px] grid gap-[20px] md:grid-cols-2 xl:grid-cols-3">
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Exchanges and exchangers</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Deposits are screened as they arrive, inside the flow that credits them, rather than in a
          separate tool a person has to remember to open.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Payment platforms</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Merchant and payout addresses pass through the same risk logic as the rest of the payment
          pipeline, at the speed the pipeline runs at.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">OTC desks and custodians</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Counterparty screening becomes part of the internal trade or custody workflow instead of
          an email thread with a report attached.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Compliance platforms</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Teams with their own case management pull AML data in and keep the case record in the
          system they already answer questions from.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Products in build</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          One integration is a realistic scope for a small team, where four separate provider
          integrations are not.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Mixed setups</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Nothing forces a choice: the API serves the machines while your analysts keep working in
          the dashboard on the same wallets.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      AML API: common questions
    </h2>
    <div class="mt-[24px] grid gap-[24px] md:grid-cols-2">
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">What is an AML API?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          An interface that lets your software ask AML questions about a crypto address or
          transaction and act on the answer automatically — the same
          <a routerLink="/aml-check" class="text-white underline underline-offset-4">AML checks</a>
          your analysts run by hand, without a human in the middle.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          What data stands behind the responses?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          The datasets of four aggregated AML data providers, including BitOK, Global Ledger and
          Beosin. That is the difference from integrating one vendor directly: you see more than one
          view of the same address.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Does it cover identity verification?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          No. AMLOfficer works with blockchain data — wallets, addresses and transactions. Document
          and identity checks stay with whatever KYC provider you use; the AML API covers the
          on-chain side of the same compliance process.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">Where is the documentation?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Technical documentation and credentials are issued with your business account rather than
          published here, and our team goes through the integration with your developers.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Can the API also start monitoring?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Yes — a wallet submitted from your system can stay under
          <a routerLink="/transaction-monitoring" class="text-white underline underline-offset-4"
            >continuous monitoring</a
          >, with alerts delivered to the dashboard and Telegram as usual.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">What does API access cost?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Terms depend on volume, so there is no single public price. AMLOfficer works on a
          pay-as-you-go model, and your plan is shown in your account after registration.
        </p>
      </div>
    </div>
  </div>
</section>

<app-business-account-cta
  heading="Put AML screening inside your product"
  text="Open a business account to get API access, connect screening and monitoring to your own systems, and keep the dashboard for the people who investigate."
></app-business-account-cta>

<app-related-pages [pages]="relatedPages"></app-related-pages>
